export function normalizeStr(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
}

function matchWords(label, words) {
  const norm = normalizeStr(label)
  return words.every((w) => norm.includes(w))
}

function filterEntry(entry, words, sectionLabel) {
  // the section label is part of each entry's searchable text, so "compute" matches
  // every entry under the "Compute" section, and "compute ec2" still narrows to EC2
  const prefix = sectionLabel ? `${sectionLabel} ` : ''
  if (entry.type === 'link') {
    return matchWords(`${prefix}${entry.label}`, words) ? entry : null
  }
  if (matchWords(`${prefix}${entry.label}`, words)) return { ...entry, searchExpanded: true }
  const items = (entry.items ?? []).filter((item) => matchWords(`${prefix}${entry.label} ${item.label}`, words))
  return items.length > 0 ? { ...entry, items, searchExpanded: true } : null
}

function filterSection(section, words) {
  const entries = section.entries.map((e) => filterEntry(e, words, section.label)).filter(Boolean)
  return entries.length > 0 ? { ...section, entries } : null
}

export function filterMenu(menu, query) {
  if (!menu) return menu
  const trimmed = (query ?? '').trim()
  if (!trimmed) return menu

  const words = normalizeStr(trimmed).split(/\s+/)

  const provider_menus = Object.fromEntries(
    (menu.providers ?? []).map((p) => [
      p,
      (menu.provider_menus[p] ?? []).map((s) => filterSection(s, words)).filter(Boolean),
    ]),
  )

  let admin = null
  if (menu.admin) {
    if (matchWords(menu.admin.label, words)) {
      admin = { ...menu.admin, searchExpanded: true }
    } else {
      const groups = (menu.admin.groups ?? [])
        .map((g) => ({
          ...g,
          items: g.items.filter((i) => matchWords(`${menu.admin.label} ${g.label} ${i.label}`, words)),
        }))
        .filter((g) => g.items.length > 0)
      if (groups.length > 0) admin = { ...menu.admin, groups, searchExpanded: true }
    }
  }

  return { ...menu, provider_menus, admin }
}

export function flattenMenuResults(menu, activeProvider = null) {
  if (!menu) return []
  const results = []

  const providers = activeProvider ? [activeProvider] : (menu.providers ?? [])

  for (const provider of providers) {
    for (const section of menu.provider_menus?.[provider] ?? []) {
      for (const entry of section.entries) {
        if (entry.type === 'link') {
          results.push({
            label: entry.label,
            href: entry.href,
            groupLabel: null,
            section: section.label,
            icon: entry.icon,
          })
        } else {
          for (const item of entry.items ?? []) {
            results.push({
              label: item.label,
              href: item.href,
              groupLabel: entry.label,
              section: section.label,
              icon: entry.icon,
            })
          }
        }
      }
    }
  }

  if (menu.admin) {
    for (const group of menu.admin.groups ?? []) {
      for (const item of group.items ?? []) {
        // admin breadcrumb reads "Admin › <subgroup>": section is the Admin label, group is the subgroup.
        // use the subgroup's own icon so results show e.g. report-money for Billing, not the generic Admin icon
        results.push({
          label: item.label,
          href: item.href,
          groupLabel: group.label,
          section: menu.admin.label,
          icon: group.icon ?? menu.admin.icon,
        })
      }
    }
  }

  return results
}

// Orders flat results by where the query matched, most specific first:
// 1) item name, 2) group name, 3) section name, 4) only a combined match.
// Stable within each tier (preserves the menu's natural order).
export function rankResults(results, query) {
  const words = normalizeStr((query ?? '').trim())
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return results
  const tier = (r) => {
    if (matchWords(r.label, words)) return 1
    if (r.groupLabel && matchWords(r.groupLabel, words)) return 2
    if (r.section && matchWords(r.section, words)) return 3
    return 4
  }
  return results
    .map((r, i) => ({ r, i, tier: tier(r) }))
    .sort((a, b) => a.tier - b.tier || a.i - b.i)
    .map((x) => x.r)
}
